// useFilterManager.js
import { useState, useMemo, useCallback } from 'react';
import { useEnumsApi } from './useEnumsApi';
import { filterService } from '../services/filterService';
import { FILTER_CONFIG } from '../components/filters/FilterConfig';

/**
 * useFilterManager V2.3 - Manual Trigger + Additional Filter Support
 * Manages "Draft" state for UI and "Applied" state for API calls.
 * 
 * Supports both standard Maintenance Filters (Array) and 
 * Additional Filters (Flat Object for Dates/Text/etc).
 */
export function useFilterManager(initialKeys = []) {
  const { maintenanceTypes, loading: loadingEnums } = useEnumsApi();

  const emptyState = initialKeys.reduce((acc, key) => ({ ...acc, [key]: null }), {});

  // 1. Draft State: Updates instantly when user clicks a dropdown
  const [draftFilters, setDraftFilters] = useState(emptyState);

  // 2. Applied State: Only updates when "Generate" is clicked
  const [appliedFilters, setAppliedFilters] = useState(emptyState);

  // Updates the UI (Draft) only
  const onFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const updated = {
        ...prev,
        [key]: value
      };

      // REPLACED: Hardcoded if-statements with Dynamic Reset Logic
      initialKeys.forEach((otherKey) => {
        const config = FILTER_CONFIG[otherKey];

        // If 'otherKey' depends on the 'key' we just changed, reset it to null
        if (config?.dependsOn?.includes(key)) {
          updated[otherKey] = null;
        }
      });

      return updated;
    });
  }, []);

  // Copies Draft to Applied - This triggers the API call in the Page component
  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  // Resets everything
  const resetFilters = useCallback(() => {
    setDraftFilters(emptyState);
    setAppliedFilters(emptyState);
  }, [emptyState]);

  /**
   * MODIFIED: Split Payload Logic
   * 1. apiFilterPayload: Handles the legacy Maintenance Array OR the Payroll DTO
   * 2. additionalApiFilterPayload: Handles flat data (Dates, Static Selects, etc), // only use this if will add additional filter that is not a maintenance
   */
  const { apiFilterPayload, additionalApiFilterPayload } = useMemo(() => {
    // A. Get the standard maintenance filters (the ones that go into the {type, value} array)
    const baseFilters = filterService.prepareMaintenancePayload(appliedFilters, maintenanceTypes);

    // B. Get the additional filters (the ones marked as ADDITIONAL in FilterConfig)
    const additionalFilters = filterService.prepareAdditionalPayload(appliedFilters);

    // C. Preserve your Payroll Module Logic
    const isPayrollModule = 'PAYROLLPERIOD' in appliedFilters;

    let mainPayload;

    if (isPayrollModule) {
      // Return the specific Payroll DTO structure
      mainPayload = {
        PayrollPeriodId: appliedFilters.PAYROLLPERIOD?.id || 0,
        PayTypeId: appliedFilters.PAYTYPE?.id || 0,
        ClassificationId: appliedFilters.CLASSIFICATION?.id || 0,
        // Map keys to PascalCase (Type/Value) to match C# DTO perfectly
        Filters: baseFilters.map(f => ({
          Type: f.type,
          Value: f.value
        }))
      };
    } else {
      // Return standard array for non-payroll modules
      mainPayload = baseFilters;
    }

    return {
      apiFilterPayload: mainPayload,
      additionalApiFilterPayload: additionalFilters
    };
  }, [appliedFilters, maintenanceTypes]);

  /**
   * ADDED: Helper to get the current UI filters formatted as 
   * Salary Rule Details for auto-populating modals.
   */
  const getFiltersAsDetails = useCallback(() => {
    return filterService.mapFiltersToRuleDetails(draftFilters, maintenanceTypes);
  }, [draftFilters, maintenanceTypes]);

  return {
    activeFilterState: draftFilters, // Linked to UI components
    loadingEnums,
    onFilterChange,
    apiFilterPayload,               // Standard/Payroll Payload
    additionalApiFilterPayload,      // ADDED: Flat Object for new filters
    applyFilters,                   // Linked to "Generate" button
    resetFilters,                   // Linked to "Clear" button
    getFiltersAsDetails,
  };
}